/*
 * Dianetics Seminar Registration
 * Shopify Basic-compatible storefront behaviour.
 *
 * This script never sends registration data directly to a third-party service.
 * It adds a Shopify product to the native cart with complete line-item properties,
 * then sends the buyer to Shopify Checkout. Shopify remains the system of record.
 */

(function () {
  'use strict';

  var TIME_ZONE = 'America/Los_Angeles';

  function query(root, selector) {
    return root.querySelector(selector);
  }

  function queryAll(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function money(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(Number(cents || 0) / 100);
  }

  function pacificDateParts(date) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      timeZoneName: 'longOffset'
    });
    var parts = formatter.formatToParts(date);
    var value = {};

    parts.forEach(function (part) {
      value[part.type] = part.value;
    });

    return value;
  }

  function pacificIsoNow() {
    var parts = pacificDateParts(new Date());
    var offset = (parts.timeZoneName || 'GMT-08:00').replace('GMT', '');

    return [parts.year, parts.month, parts.day].join('-') + 'T' +
      [parts.hour, parts.minute, parts.second].join(':') + offset;
  }

  function pacificFriendlyNow() {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    }).format(new Date());
  }

  function pacificDateKey(date) {
    var parts = pacificDateParts(date);
    return [parts.year, parts.month, parts.day].join('-');
  }

  function addMonthsToDateKey(dateKey, months) {
    var parts = dateKey.split('-').map(function (part) { return Number(part); });
    var date = new Date(Date.UTC(parts[0], parts[1] - 1 + months, parts[2]));
    return date.getUTCFullYear() + '-' + String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + String(date.getUTCDate()).padStart(2, '0');
  }

  function parseSeminarRecords(root) {
    var source = query(root, '[data-seminar-records]');
    if (!source) return [];

    try {
      var raw = JSON.parse(source.textContent || '[]');
      var today = pacificDateKey(new Date());
      var cutoff = addMonthsToDateKey(today, Number(root.dataset.calendarMonths || 6));

      return raw
        .filter(function (seminar) {
          return seminar &&
            seminar.available !== false &&
            seminar.seminar_type === 'weekend' &&
            typeof seminar.start_iso === 'string' &&
            seminar.start_iso.slice(0, 10) >= today &&
            seminar.start_iso.slice(0, 10) <= cutoff;
        })
        .sort(function (a, b) {
          return a.start_iso.localeCompare(b.start_iso);
        });
    } catch (error) {
      console.warn('Seminar registration could not read the seminar Metaobjects.', error);
      return [];
    }
  }

  function initialize(root) {
    var form = query(root, '[data-seminar-form]');
    if (!form) return;

    var regularVariantId = root.dataset.regularVariantId;
    var groupVariantId = root.dataset.groupVariantId;
    var regularPrice = Number(root.dataset.regularPrice || 50000);
    var groupPrice = Number(root.dataset.groupPrice || 37500);
    var maxAttendees = Number(root.dataset.maxAttendees || 20);
    var nextOptionsCount = Number(root.dataset.nextOptionsCount || 12);
    var seminars = parseSeminarRecords(root);

    var primaryFirstName = query(root, '[data-primary-first-name]');
    var primaryLastName = query(root, '[data-primary-last-name]');
    var primaryEmail = query(root, '[data-primary-email]');
    var primaryPhone = query(root, '[data-primary-phone]');
    var languageSelect = query(root, '[data-language]');
    var attendeeCountSelect = query(root, '[data-attendee-count]');
    var customAttendeeCount = query(root, '[data-custom-attendee-count]');
    var quantityOther = query(root, '[data-quantity-other]');
    var additionalAttendees = query(root, '[data-additional-attendees]');
    var additionalAttendeesList = query(root, '[data-additional-attendees-list]');
    var specificSelect = query(root, '[data-specific-seminar]');
    var datePicker = query(root, '[data-date-picker]');
    var calendar = query(root, '[data-seminar-calendar]');
    var calendarToggle = query(root, '[data-calendar-toggle]');
    var calendarDates = query(root, '[data-calendar-dates]');
    var submitButton = query(root, '[data-submit-registration]');
    var notice = query(root, '[data-form-notice]');

    var summarySeminarTitle = query(root, '[data-summary-seminar-title]');
    var summarySeminarDate = query(root, '[data-summary-seminar-date]');
    var summaryCount = query(root, '[data-summary-attendee-count]');
    var summaryPrice = query(root, '[data-summary-price-per-attendee]');
    var summaryPayment = query(root, '[data-summary-payment]');
    var summaryTotal = query(root, '[data-summary-total]');

    function currentMode() {
      var checked = query(root, '[data-seminar-mode]:checked');
      return checked ? checked.value : 'next_available';
    }

    function currentPayment() {
      var checked = query(root, '[data-payment-method]:checked');
      return checked ? checked.value : 'pay_online';
    }

    function currentAttendeeCount() {
      if (attendeeCountSelect.value === '5plus') {
        var customValue = Number(customAttendeeCount.value || 5);
        return Math.max(5, Math.min(maxAttendees, Math.floor(customValue)));
      }
      return Math.max(1, Number(attendeeCountSelect.value || 1));
    }

    function currentPricePerAttendee() {
      return currentAttendeeCount() >= 2 ? groupPrice : regularPrice;
    }

    function findSeminar(handle) {
      return seminars.find(function (seminar) { return seminar.handle === handle; }) || null;
    }

    function currentSeminar() {
      var mode = currentMode();
      if (mode === 'schedule_later') return null;
      if (mode === 'specific_weekend') return findSeminar(specificSelect.value);
      return seminars.length ? seminars[0] : null;
    }

    function selectionFriendlyText(mode) {
      if (mode === 'specific_weekend') return 'Specific Weekend';
      if (mode === 'schedule_later') return 'Schedule Later';
      return 'Next Available Weekend Seminar';
    }

    function paymentFriendlyText(payment) {
      return payment === 'pay_at_seminar' ? 'Pay at the Seminar' : 'Pay Online Today';
    }

    function seminarState() {
      var mode = currentMode();
      var seminar = currentSeminar();

      if (mode === 'schedule_later') {
        return {
          selectionMachine: 'schedule_later',
          selectionFriendly: selectionFriendlyText(mode),
          statusMachine: 'date_pending',
          statusFriendly: 'Date Pending',
          startIso: 'date_pending',
          endIso: 'date_pending',
          friendlyLabel: 'Date Pending'
        };
      }

      if (!seminar) {
        return {
          selectionMachine: mode,
          selectionFriendly: selectionFriendlyText(mode),
          statusMachine: 'date_unavailable',
          statusFriendly: 'Seminar Date Unavailable',
          startIso: '',
          endIso: '',
          friendlyLabel: 'Select an available seminar weekend'
        };
      }

      return {
        selectionMachine: mode,
        selectionFriendly: selectionFriendlyText(mode),
        statusMachine: 'seat_reserved',
        statusFriendly: 'Seat Reserved',
        startIso: seminar.start_iso,
        endIso: seminar.end_iso || '',
        friendlyLabel: seminar.friendly_label || seminar.start_iso.slice(0, 10),
        handle: seminar.handle
      };
    }

    function hideNotice() {
      notice.hidden = true;
      notice.textContent = '';
    }

    function showNotice(message) {
      notice.textContent = message;
      notice.hidden = false;
      notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function populateDateControls() {
      var nextOptions = seminars.slice(0, nextOptionsCount);
      specificSelect.innerHTML = '<option value="" selected disabled>Select a seminar weekend</option>' +
        nextOptions.map(function (seminar) {
          return '<option value="' + escapeHtml(seminar.handle) + '">' + escapeHtml(seminar.friendly_label || seminar.start_iso.slice(0, 10)) + '</option>';
        }).join('');

      calendarDates.innerHTML = seminars.map(function (seminar) {
        var title = seminar.friendly_label || seminar.start_iso.slice(0, 10);
        return '<label class="dsr-date-card">' +
          '<input type="radio" name="calendar-seminar-' + escapeHtml(root.id || 'registration') + '" value="' + escapeHtml(seminar.handle) + '" data-calendar-seminar>' +
          '<span class="dsr-date-card__title">' + escapeHtml(title) + '</span>' +
          '<span class="dsr-date-card__meta">Weekend Seminar</span>' +
        '</label>';
      }).join('');
    }

    function renderAdditionalAttendees() {
      var count = currentAttendeeCount();
      var numberAdditional = count - 1;
      var existing = {};

      queryAll(root, '[data-additional-first-name]').forEach(function (input) {
        var index = input.dataset.additionalFirstName;
        existing[index] = existing[index] || {};
        existing[index].firstName = input.value;
      });
      queryAll(root, '[data-additional-last-name]').forEach(function (input) {
        var index = input.dataset.additionalLastName;
        existing[index] = existing[index] || {};
        existing[index].lastName = input.value;
      });

      additionalAttendees.hidden = numberAdditional < 1;
      if (numberAdditional < 1) {
        additionalAttendeesList.innerHTML = '';
        return;
      }

      var cards = [];
      for (var index = 1; index <= numberAdditional; index += 1) {
        var previous = existing[index] || {};
        cards.push(
          '<div class="dsr-attendee-card">' +
            '<p class="dsr-attendee-card__title">Attendee ' + (index + 1) + '</p>' +
            '<div class="dsr-form__grid">' +
              '<div class="dsr-form__field">' +
                '<label for="DsrAdditionalFirst-' + escapeHtml(root.id) + '-' + index + '">First Name <span aria-hidden="true">*</span></label>' +
                '<input id="DsrAdditionalFirst-' + escapeHtml(root.id) + '-' + index + '" type="text" data-additional-first-name="' + index + '" value="' + escapeHtml(previous.firstName || '') + '" required>' +
              '</div>' +
              '<div class="dsr-form__field">' +
                '<label for="DsrAdditionalLast-' + escapeHtml(root.id) + '-' + index + '">Last Name <span aria-hidden="true">*</span></label>' +
                '<input id="DsrAdditionalLast-' + escapeHtml(root.id) + '-' + index + '" type="text" data-additional-last-name="' + index + '" value="' + escapeHtml(previous.lastName || '') + '" required>' +
              '</div>' +
            '</div>' +
          '</div>'
        );
      }
      additionalAttendeesList.innerHTML = cards.join('');
    }

    function updateModeControls() {
      var mode = currentMode();
      datePicker.hidden = mode !== 'specific_weekend';
      if (mode !== 'specific_weekend') {
        specificSelect.value = '';
      }
    }

    function updateCustomCountControl() {
      var isCustom = attendeeCountSelect.value === '5plus';
      quantityOther.hidden = !isCustom;
      customAttendeeCount.required = isCustom;
      if (!isCustom) customAttendeeCount.value = '5';
    }

    function updateSummary() {
      var count = currentAttendeeCount();
      var price = currentPricePerAttendee();
      var state = seminarState();
      var payment = currentPayment();

      summarySeminarTitle.textContent = state.selectionFriendly;
      summarySeminarDate.textContent = state.friendlyLabel;
      summaryCount.textContent = String(count);
      summaryPrice.textContent = money(price);
      summaryPayment.textContent = paymentFriendlyText(payment);
      summaryTotal.textContent = money(count * price);
    }

    function refresh() {
      updateModeControls();
      updateCustomCountControl();
      renderAdditionalAttendees();
      updateSummary();
    }

    function collectAdditionalAttendees() {
      var attendees = [];
      var firstNames = queryAll(root, '[data-additional-first-name]');
      firstNames.forEach(function (firstInput) {
        var index = firstInput.dataset.additionalFirstName;
        var lastInput = query(root, '[data-additional-last-name="' + index + '"]');
        attendees.push({
          first_name: firstInput.value.trim(),
          last_name: lastInput ? lastInput.value.trim() : ''
        });
      });
      return attendees;
    }

    function validate() {
      hideNotice();
      var requiredFields = queryAll(root, 'input[required], select[required]');
      var invalid = requiredFields.find(function (field) {
        return !field.checkValidity();
      });

      if (invalid) {
        invalid.reportValidity();
        return false;
      }

      var mode = currentMode();
      if (mode === 'specific_weekend' && !specificSelect.value) {
        showNotice('Please select an available seminar weekend.');
        specificSelect.focus();
        return false;
      }

      if ((mode === 'next_available' || mode === 'specific_weekend') && !currentSeminar()) {
        showNotice('There are no available seminar weekends to select. Please choose “Schedule Later” or contact the Foundation for assistance.');
        return false;
      }

      return true;
    }

    function registrationPropertiesOLD() {
      var languageOption = languageSelect.options[languageSelect.selectedIndex];
      var languageMachine = languageSelect.value;
      var languageFriendly = languageOption ? languageOption.dataset.friendly || languageOption.text : '';
      var count = currentAttendeeCount();
      var price = currentPricePerAttendee();
      var paymentMachine = currentPayment();
      var paymentFriendly = paymentFriendlyText(paymentMachine);
      var state = seminarState();
      var additional = collectAdditionalAttendees();
      var submittedIso = pacificIsoNow();
      var submittedFriendly = pacificFriendlyNow();

      return {
        'Registration Type (Machine)': 'dianetics_seminar',
        'Registration Type': 'Dianetics Seminar Registration',
        'Registration Version': 'seminar_v1',
        'Primary First Name': primaryFirstName.value.trim(),
        'Primary Last Name': primaryLastName.value.trim(),
        'Primary Email': primaryEmail.value.trim(),
        'Primary Mobile Phone': primaryPhone.value.trim(),
        'Preferred Language (Machine)': languageMachine,
        'Preferred Language': languageFriendly,
        'Seminar Selection (Machine)': state.selectionMachine,
        'Seminar Selection': state.selectionFriendly,
        'Seminar Start (ISO-8601 PT)': state.startIso,
        'Seminar End (ISO-8601 PT)': state.endIso,
        'Seminar Weekend': state.friendlyLabel,
        'Seminar Status (Machine)': state.statusMachine,
        'Seminar Status': state.statusFriendly,
        'Attendee Count': String(count),
        'Price Per Attendee': money(price),
        'Total Registration': money(count * price),
        'Additional Attendees': additional.length ? additional.map(function (attendee) { return attendee.first_name + ' ' + attendee.last_name; }).join(' | ') : 'None',
        'Additional Attendees (JSON)': JSON.stringify(additional),
        'Payment Preference (Machine)': paymentMachine,
        'Payment Preference': paymentFriendly,
        'Registration Submitted (ISO-8601 PT)': submittedIso,
        'Registration Submitted': submittedFriendly,
        'Seminar Time Zone': TIME_ZONE
      };
    }

    function registrationProperties() {
      var languageOption = languageSelect.options[languageSelect.selectedIndex];
      var languageMachine = languageSelect.value;
      var languageFriendly = languageOption ? languageOption.dataset.friendly || languageOption.text : '';
      var count = currentAttendeeCount();
      var price = currentPricePerAttendee();
      var paymentMachine = currentPayment();
      var paymentFriendly = paymentFriendlyText(paymentMachine);
      var state = seminarState();
      var additional = collectAdditionalAttendees();
      var submittedIso = pacificIsoNow();
      var submittedFriendly = pacificFriendlyNow();

      return {
  // Visible to customer
  'Seminar': state.selectionFriendly,
  'Seminar Weekend': state.friendlyLabel,
  'Preferred Language': languageFriendly,
  'Attendees': String(count),
  'Total Registration': money(count * price),

  // Internal
  '_Registration Type': 'Dianetics Seminar Registration',
  '_Registration Version': 'seminar_v1',
  '_Primary First Name': primaryFirstName.value.trim(),
  '_Primary Last Name': primaryLastName.value.trim(),
  '_Primary Email': primaryEmail.value.trim(),
  '_Primary Mobile Phone': primaryPhone.value.trim(),
  '_Preferred Language (Machine)': languageSelect.value,
  '_Seminar Selection (Machine)': state.selectionMachine,
  '_Seminar Start': state.startIso,
  '_Seminar End': state.endIso,
  '_Seminar Status': state.statusMachine,
  '_Additional Attendees': JSON.stringify(collectAdditionalAttendees()),
  '_Payment Preference (Machine)': currentPayment(),
  '_Submitted': pacificIsoNow(),
  '_Time Zone': TIME_ZONE
};
    }

    

    async function addToCartAndCheckout() {
      if (!validate()) return;

      var count = currentAttendeeCount();
      var usesGroupRate = count >= 2;
      var variantId = usesGroupRate ? groupVariantId : regularVariantId;

      if (!variantId) {
        showNotice('The seminar products have not been configured in this theme section. Please contact the site administrator.');
        return;
      }

      submitButton.disabled = true;
      submitButton.textContent = 'Preparing Secure Checkout…';

      var payload = {
        items: [{
          id: Number(variantId),
          quantity: count,
          properties: registrationProperties()
        }]
      };

      try {
        var response = await fetch(root.dataset.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          var errorData = await response.json().catch(function () { return {}; });
          throw new Error(errorData.description || 'Shopify could not add the seminar registration to the cart.');
        }

        window.location.assign(root.dataset.checkoutUrl);
      } catch (error) {
        console.error('Seminar registration cart error:', error);
        showNotice(error.message || 'We could not begin checkout. Please try again.');
        submitButton.disabled = false;
        submitButton.textContent = 'Continue to Secure Checkout';
      }
    }

    populateDateControls();
    refresh();

    queryAll(root, '[data-seminar-mode], [data-payment-method]').forEach(function (input) {
      input.addEventListener('change', refresh);
    });

    attendeeCountSelect.addEventListener('change', refresh);
    customAttendeeCount.addEventListener('input', function () {
      var value = Math.max(5, Math.min(maxAttendees, Number(customAttendeeCount.value || 5)));
      if (String(value) !== customAttendeeCount.value && customAttendeeCount.value !== '') {
        customAttendeeCount.value = String(value);
      }
      renderAdditionalAttendees();
      updateSummary();
    });

    specificSelect.addEventListener('change', updateSummary);

    calendarToggle.addEventListener('click', function () {
      calendar.hidden = !calendar.hidden;
      calendarToggle.setAttribute('aria-expanded', String(!calendar.hidden));
    });

    calendarDates.addEventListener('change', function (event) {
      var input = event.target.closest('[data-calendar-seminar]');
      if (!input) return;
      specificSelect.value = input.value;
      query(root, '[data-seminar-mode][value="specific_weekend"]').checked = true;
      calendar.hidden = true;
      calendarToggle.setAttribute('aria-expanded', 'false');
      refresh();
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      addToCartAndCheckout();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    queryAll(document, '[data-seminar-registration]').forEach(initialize);
  });
}());
